import { useCallback } from 'react'
import { useResource } from '@/components/admin/shared/hooks/use-resource'
import type { RouterOutputs } from '@/lib/trpc'
import { trpc } from '../../../../lib/trpc.js'
import type { Task, TaskFormData } from '../types'

interface UseTasksOptions {
  sourceId?: string
}

interface UseTasksReturn {
  tasks: Task[]
  loading: boolean
  error: string | null
  total: number
  createTask: (data: TaskFormData) => Promise<Task>
  updateTask: (id: string, data: Partial<TaskFormData>) => Promise<Task>
  deleteTask: (id: string) => Promise<void>
  refresh: () => Promise<void>
}

const selectTasks = (
  data: RouterOutputs['admin']['content']['listTasks'] | undefined,
): Task[] => data?.data ?? []

export function useTasks(options: UseTasksOptions = {}): UseTasksReturn {
  const utils = trpc.useUtils()

  const queryInput = options.sourceId
    ? { sourceId: options.sourceId }
    : undefined

  const invalidate = useCallback(() => {
    utils.admin.content.listTasks.invalidate()
  }, [utils])

  const {
    items: tasks,
    data,
    loading,
    error,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.content.listTasks.useQuery(queryInput),
    select: selectTasks,
    invalidate,
    create: trpc.admin.content.createTask,
    update: trpc.admin.content.updateTask,
    remove: trpc.admin.content.deleteTask,
  })

  const createTask = useCallback(
    async (formData: TaskFormData): Promise<Task> => {
      const created = await create({
        title: formData.title,
        notes: formData.notes || null,
        url: formData.url || null,
        category: formData.category || null,
        estimatedMinutes: formData.estimatedMinutes,
        sourceId: formData.sourceId,
        sourceContentId: formData.sourceContentId,
        contentBody: formData.contentBody || null,
      })
      return created as Task
    },
    [create],
  )

  const updateTask = useCallback(
    async (id: string, formData: Partial<TaskFormData>): Promise<Task> => {
      const updated = await update({ id, ...formData })
      return updated as Task
    },
    [update],
  )

  const deleteTask = useCallback(
    async (id: string): Promise<void> => {
      await remove({ id })
    },
    [remove],
  )

  const refresh = useCallback(async () => {
    await utils.admin.content.listTasks.invalidate()
  }, [utils])

  return {
    tasks,
    loading,
    error,
    total: data?.total ?? 0,
    createTask,
    updateTask,
    deleteTask,
    refresh,
  }
}
